import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Avatar, Card, CardContent, Grid, Chip, IconButton,
  Tooltip, CircularProgress, Alert, InputAdornment, Divider,
  Table, TableBody, TableCell, TableHead, TableRow, TablePagination,
  Stepper, Step, StepLabel, Fade, Stack, alpha,
  ToggleButton, ToggleButtonGroup, Checkbox, Switch, FormControlLabel,
} from '@mui/material';
import {
  Add, Refresh, Visibility, VisibilityOff, ContentCopy, DoneAll,
  CheckCircle, Cancel, CloudUpload, Logout, GridView, TableRows,
  Search, People, TrendingUp, PowerSettingsNew,
  DirectionsCar, LocalParking, Edit, InfoOutlined, Videocam, Delete,
  CameraAlt, ManageAccounts, PhoneIphone, AdminPanelSettings, VerifiedUser,
} from '@mui/icons-material';
import {
  Select, MenuItem, FormControl, InputLabel, Tabs, Tab,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/axios';

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_API_URL || ''}${url}`;
};

const PRIMARY   = '#282626';
const ORANGE    = '#F59E0B';
const BLUE      = '#3b82f6';
const GREEN     = '#22c55e';
const WHITE     = '#FFFFFF';
const BG        = '#F4F4F4';
const CARD_BG   = '#FFFFFF';
const BORDER    = '#E5E5E5';
const TEXT_SEC  = '#6B6B6B';

const api = {
  listar:       () => apiClient.get('/api/clientes/'),
  crear:        (d) => apiClient.post('/api/clientes/', d),
  actualizar:   (id, d) => apiClient.put(`/api/clientes/${id}`, d),
  toggleActivo: (id, activo) => apiClient.patch(`/api/clientes/${id}/activar`, { activo }),
  eliminar:     (id, password) => apiClient.delete(`/api/clientes/${id}`, { data: { password } }),
};

// ─── API Key ──────────────────────────────────────────────────────────────────
function ApiKeyBox({ apiKey }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(apiKey); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: BG, borderRadius: 1.5, px: 1.5, py: 0.75, border: `1px solid ${BORDER}` }}>
      <Typography sx={{ fontFamily: 'monospace', fontSize: 11, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: TEXT_SEC }}>
        {visible ? apiKey : '••••••••••••••••••••••••••••'}
      </Typography>
      <IconButton size="small" onClick={() => setVisible(v => !v)} sx={{ p: 0.3, color: TEXT_SEC }}>
        {visible ? <VisibilityOff sx={{ fontSize: 14 }} /> : <Visibility sx={{ fontSize: 14 }} />}
      </IconButton>
      <Tooltip title={copied ? '¡Copiado!' : 'Copiar'}>
        <IconButton size="small" onClick={copy} sx={{ p: 0.3, color: copied ? ORANGE : TEXT_SEC }}>
          {copied ? <DoneAll sx={{ fontSize: 14 }} /> : <ContentCopy sx={{ fontSize: 14 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// ─── Diálogo editar cliente ───────────────────────────────────────────────────
function EditarClienteDialog({ open, cliente, onClose, onGuardado }) {
  const [nombre, setNombre] = useState('');
  const [espacios, setEspacios] = useState(0);
  const [latiniumEnabled, setLatiniumEnabled] = useState(false);
  const [logoBase64, setLogoBase64] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (cliente) {
      setNombre(cliente.nombre || '');
      setEspacios(cliente.total_parking_spaces || 0);
      setLatiniumEnabled(!!cliente.latinium_enabled);
      setLogoPreview(cliente.logo_url || '');
      setLogoBase64('');
      setError('');
    }
  }, [cliente]);

  const handleLogo = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setLogoPreview(ev.target.result); setLogoBase64(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleGuardar = async () => {
    if (!nombre.trim()) { setError('El nombre es requerido'); return; }
    setCargando(true); setError('');
    try {
      await api.actualizar(cliente.id, {
        nombre: nombre.trim(),
        logo_base64: logoBase64 || undefined,
        total_parking_spaces: Number(espacios) || 0,
        latinium_enabled: latiniumEnabled,
      });
      onGuardado();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Error actualizando');
    } finally {
      setCargando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: PRIMARY, px: 3, py: 2 }}>
        <Typography fontWeight={700} sx={{ color: WHITE }}>Editar parqueadero</Typography>
        <Typography variant="caption" sx={{ color: alpha(WHITE, 0.6) }}>{cliente?.nombre}</Typography>
      </Box>
      <DialogContent sx={{ pt: 2.5, px: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
          <Avatar src={logoPreview} sx={{ width: 56, height: 56, bgcolor: alpha(ORANGE, 0.12), color: ORANGE, fontWeight: 800, fontSize: 18 }}>
            {nombre ? nombre.slice(0, 2).toUpperCase() : '?'}
          </Avatar>
          <Button component="label" size="small" variant="outlined" startIcon={<CloudUpload />}
            sx={{ borderColor: ORANGE, color: ORANGE, borderRadius: 2, '&:hover': { bgcolor: alpha(ORANGE, 0.06) } }}>
            Cambiar logo
            <input type="file" hidden accept="image/*" onChange={handleLogo} />
          </Button>
        </Box>

        <TextField fullWidth label="Nombre del parqueadero" value={nombre}
          onChange={e => setNombre(e.target.value)} autoFocus sx={{ mb: 2 }} />
        <TextField fullWidth label="Número de espacios de estacionamiento" type="number"
          value={espacios} onChange={e => setEspacios(e.target.value)}
          slotProps={{ htmlInput: { min: 0 } }}
          helperText="Capacidad total del parqueadero" sx={{ mb: 2 }} />

        <FormControlLabel
          control={
            <Switch
              checked={latiniumEnabled}
              onChange={e => setLatiniumEnabled(e.target.checked)}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ORANGE }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ORANGE } }}
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>Facturación electrónica (Latinium)</Typography>
              <Typography variant="caption" sx={{ color: TEXT_SEC }}>Activar para emitir facturas electrónicas automáticamente al cobrar</Typography>
            </Box>
          }
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: TEXT_SEC }}>Cancelar</Button>
        <Button variant="contained" onClick={handleGuardar} disabled={cargando}
          startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ bgcolor: ORANGE, color: WHITE, borderRadius: 2, fontWeight: 700, '&:hover': { bgcolor: '#D97706' } }}>
          {cargando ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, loading }) {
  return (
    <Card elevation={0} sx={{ flex: 1, minWidth: 150, bgcolor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 2 }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: alpha(ORANGE, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {React.cloneElement(icon, { sx: { color: ORANGE, fontSize: 24 } })}
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: TEXT_SEC, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
          {loading
            ? <Box sx={{ width: 32, height: 24, bgcolor: BG, borderRadius: 1, mt: 0.25 }} />
            : <Typography variant="h5" fontWeight={700} sx={{ color: PRIMARY, lineHeight: 1.2 }}>{value}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Diálogo cámaras ─────────────────────────────────────────────────────────
const CAM_VACIA = { nombre: '', tipo: 'entrada', ip_address: '', puerto: 80, usuario: 'admin', password: '', timeout_segundos: 10, reintentos: 3, notas: '', sucursal_id: '' };

function CamarasDialog({ open, cliente, onClose }) {
  const [camaras, setCamaras] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(CAM_VACIA);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(async () => {
    if (!cliente) return;
    setLoading(true);
    try {
      const [rc, rs] = await Promise.all([
        apiClient.get(`/api/clientes/${cliente.id}/camaras`),
        apiClient.get(`/api/clientes/${cliente.id}/sucursales`),
      ]);
      setCamaras(rc.data.data || []);
      setSucursales(rs.data.data || []);
    } finally { setLoading(false); }
  }, [cliente]);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);

  const handleSave = async () => {
    if (!form.nombre || !form.ip_address) { setError('Nombre e IP son obligatorios'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, sucursal_id: form.sucursal_id || null };
      if (editId) {
        await apiClient.put(`/api/clientes/${cliente.id}/camaras/${editId}`, payload);
      } else {
        await apiClient.post(`/api/clientes/${cliente.id}/camaras`, payload);
      }
      setForm(CAM_VACIA); setEditId(null);
      await cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error guardando'); }
    finally { setSaving(false); }
  };

  const handleEdit = (cam) => { setForm({ ...cam, password: '', sucursal_id: cam.sucursal_id ?? '' }); setEditId(cam.id); };
  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta cámara?')) return;
    await apiClient.delete(`/api/clientes/${cliente.id}/camaras/${id}`);
    await cargar();
  };
  const cancelEdit = () => { setForm(CAM_VACIA); setEditId(null); setError(''); };

  const inputSx = { mb: 1.5 };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: PRIMARY, px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Videocam sx={{ color: ORANGE }} />
        <Box>
          <Typography fontWeight={700} sx={{ color: WHITE }}>Cámaras — {cliente?.nombre}</Typography>
          <Typography variant="caption" sx={{ color: alpha(WHITE, 0.5) }}>Configura las cámaras ANPR del parqueadero</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', height: 480 }}>
        {/* Formulario izq */}
        <Box sx={{ width: 300, flexShrink: 0, p: 2.5, borderRight: `1px solid ${BORDER}`, overflowY: 'auto' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: PRIMARY, mb: 1.5 }}>
            {editId ? 'Editar cámara' : 'Nueva cámara'}
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, fontSize: 12 }}>{error}</Alert>}

          <TextField fullWidth size="small" label="Nombre" value={form.nombre}
            onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))} sx={inputSx} />

          <FormControl fullWidth size="small" sx={inputSx}>
            <InputLabel>Tipo</InputLabel>
            <Select value={form.tipo} label="Tipo" onChange={e => setForm(p => ({ ...p, tipo: e.target.value }))}>
              <MenuItem value="entrada">Entrada</MenuItem>
              <MenuItem value="salida">Salida</MenuItem>
            </Select>
          </FormControl>

          <TextField fullWidth size="small" label="IP / Host" value={form.ip_address}
            onChange={e => setForm(p => ({ ...p, ip_address: e.target.value }))} sx={inputSx}
            placeholder="192.168.1.100" />

          <TextField fullWidth size="small" label="Puerto" type="number" value={form.puerto}
            onChange={e => setForm(p => ({ ...p, puerto: parseInt(e.target.value) || 80 }))} sx={inputSx} />

          <TextField fullWidth size="small" label="Usuario" value={form.usuario}
            onChange={e => setForm(p => ({ ...p, usuario: e.target.value }))} sx={inputSx} />

          <TextField fullWidth size="small" label={editId ? 'Contraseña (dejar en blanco = sin cambios)' : 'Contraseña'}
            type="password" value={form.password}
            onChange={e => setForm(p => ({ ...p, password: e.target.value }))} sx={inputSx} />

          {sucursales.length > 0 && (
            <FormControl fullWidth size="small" sx={inputSx}>
              <InputLabel>Sucursal</InputLabel>
              <Select value={form.sucursal_id} label="Sucursal" onChange={e => setForm(p => ({ ...p, sucursal_id: e.target.value }))}>
                <MenuItem value="">Todo el parqueadero</MenuItem>
                {sucursales.map(s => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            {editId && <Button size="small" onClick={cancelEdit} sx={{ flex: 1, borderRadius: 2, color: TEXT_SEC }}>Cancelar</Button>}
            <Button fullWidth={!editId} size="small" variant="contained" onClick={handleSave} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Add />}
              sx={{ flex: 1, borderRadius: 2, bgcolor: PRIMARY, fontWeight: 700, '&:hover': { bgcolor: '#1a1a1a' } }}>
              {saving ? 'Guardando...' : editId ? 'Actualizar' : 'Agregar'}
            </Button>
          </Box>
        </Box>

        {/* Lista der */}
        <Box sx={{ flex: 1, p: 2.5, overflowY: 'auto' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: PRIMARY, mb: 1.5 }}>
            Cámaras registradas ({camaras.length})
          </Typography>
          {loading ? <CircularProgress size={24} sx={{ color: ORANGE }} /> : camaras.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, color: TEXT_SEC }}>
              <CameraAlt sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
              <Typography variant="body2">Sin cámaras configuradas</Typography>
            </Box>
          ) : camaras.map(cam => (
            <Card key={cam.id} elevation={0} sx={{ mb: 1.5, border: `1px solid ${BORDER}`, borderRadius: 2, borderLeft: `4px solid ${cam.tipo === 'entrada' ? ORANGE : BLUE}` }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ color: PRIMARY }}>{cam.nombre}</Typography>
                      <Chip size="small" label={cam.tipo} sx={{ height: 18, fontSize: 9, fontWeight: 700,
                        bgcolor: cam.tipo === 'entrada' ? alpha(ORANGE, 0.1) : alpha(BLUE, 0.1),
                        color: cam.tipo === 'entrada' ? ORANGE : BLUE }} />
                      <Chip size="small" label={cam.activa ? 'Activa' : 'Inactiva'} sx={{ height: 18, fontSize: 9, fontWeight: 700,
                        bgcolor: cam.activa ? alpha(GREEN, 0.1) : alpha(TEXT_SEC, 0.1),
                        color: cam.activa ? GREEN : TEXT_SEC }} />
                      {cam.sucursal_id && (
                        <Chip size="small" label={sucursales.find(s => s.id === cam.sucursal_id)?.nombre || 'Sucursal'}
                          sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: alpha(ORANGE, 0.1), color: ORANGE }} />
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ color: TEXT_SEC, fontFamily: 'monospace' }}>
                      {cam.ip_address}:{cam.puerto} · {cam.usuario}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEdit(cam)}><Edit sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => handleDelete(cam.id)}><Delete sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, color: TEXT_SEC }}>Cerrar</Button>
      </Box>
    </Dialog>
  );
}

// ─── Diálogo confirmación eliminar parqueadero ────────────────────────────────
function ConfirmDeleteParqueadero({ cliente, open, onClose, onConfirm, deleting }) {
  const [password, setPassword]   = useState('');
  const [showPass, setShowPass]   = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    if (open) { setPassword(''); setShowPass(false); setError(''); }
  }, [open]);

  const handleConfirm = async () => {
    if (!password) { setError('Ingresa tu contraseña'); return; }
    setError('');
    try {
      await onConfirm(password);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al eliminar');
    }
  };

  if (!cliente) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: '#FEF2F2', px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Delete sx={{ color: '#DC2626', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography fontWeight={700} sx={{ color: '#DC2626', fontSize: 15 }}>Eliminar parqueadero</Typography>
          <Typography variant="caption" sx={{ color: '#888' }}>Esta acción es permanente e irreversible</Typography>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        {/* Campos señuelo: evitan que el browser llene el email en la barra de búsqueda */}
        <input type="text"     autoComplete="username"     style={{ display: 'none' }} readOnly />
        <input type="password" autoComplete="new-password" style={{ display: 'none' }} readOnly />

        <Typography variant="body2" sx={{ color: '#444', mb: 1.5 }}>
          Estás a punto de eliminar <strong>"{cliente.nombre}"</strong> y todos sus datos:
          registros de entrada/salida, cámaras y asignaciones de usuarios.
        </Typography>

        <Box sx={{ bgcolor: alpha('#DC2626', 0.05), border: `1px solid ${alpha('#DC2626', 0.15)}`, borderRadius: 2, px: 2, py: 1.5, mb: 2.5 }}>
          {[
            'Todos los registros vehiculares del parqueadero',
            'Cámaras ANPR configuradas',
            'Asignación de usuarios (los usuarios no se eliminan)',
          ].map(txt => (
            <Box key={txt} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
              <Typography sx={{ color: '#DC2626', fontSize: 14, lineHeight: 1.6 }}>•</Typography>
              <Typography variant="caption" sx={{ color: '#7f1d1d', lineHeight: 1.6 }}>{txt}</Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="caption" fontWeight={700} sx={{ color: PRIMARY, display: 'block', mb: 1 }}>
          Confirma con tu contraseña de superadmin
        </Typography>
        <TextField
          fullWidth size="small"
          type={showPass ? 'text' : 'password'}
          placeholder="Tu contraseña"
          value={password}
          onChange={e => { setPassword(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && !deleting && handleConfirm()}
          error={!!error}
          helperText={error}
          autoFocus
          inputProps={{ autoComplete: 'new-password' }}
          InputProps={{
            sx: { borderRadius: 2 },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPass(v => !v)}>
                  {showPass ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: '#888', borderRadius: 2 }}>Cancelar</Button>
        <Button variant="contained" onClick={handleConfirm}
          disabled={deleting || !password}
          startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <Delete />}
          sx={{ bgcolor: '#DC2626', borderRadius: 2, fontWeight: 700, color: 'white', '&:hover': { bgcolor: '#b91c1c' }, minWidth: 140 }}>
          {deleting ? 'Eliminando…' : 'Eliminar todo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Cliente Card ─────────────────────────────────────────────────────────────
function ClienteCard({ cliente, onActualizar, clientes, puedeEliminar = true }) {
  const [cargando, setCargando]         = useState(false);
  const [editOpen, setEditOpen]         = useState(false);
  const [camarasOpen, setCamarasOpen]   = useState(false);
  const [usuariosOpen, setUsuariosOpen] = useState(false);
  const [deleteOpen, setDeleteOpen]     = useState(false);
  const [deleting, setDeleting]         = useState(false);
  const toggle = async () => { setCargando(true); try { await api.toggleActivo(cliente.id, !cliente.activo); onActualizar(); } finally { setCargando(false); } };
  const handleDelete = async (password) => {
    setDeleting(true);
    try {
      await api.eliminar(cliente.id, password);
      setDeleteOpen(false);
      onActualizar();
    } finally {
      setDeleting(false);
    }
  };
  const initials = cliente.nombre.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <Card elevation={0} sx={{
      bgcolor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 2,
      borderTop: `4px solid ${cliente.activo ? ORANGE : BORDER}`,
      transition: 'all 0.2s',
      '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.10)', transform: 'translateY(-2px)' },
      opacity: cliente.activo ? 1 : 0.65,
    }}>
      <CardContent sx={{ p: 2.5 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
          <Avatar src={resolveUrl(cliente.logo_url)} sx={{ width: 50, height: 50, bgcolor: alpha(ORANGE, 0.12), color: ORANGE, fontWeight: 800, fontSize: 16, border: `2px solid ${alpha(ORANGE, 0.2)}`, flexShrink: 0 }}>
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: PRIMARY }} noWrap>{cliente.nombre}</Typography>
            <Typography variant="caption" sx={{ color: TEXT_SEC, fontFamily: 'monospace' }}>/{cliente.slug}</Typography>
          </Box>
          <Chip size="small" label={cliente.activo ? 'Activo' : 'Inactivo'}
            sx={{ fontWeight: 700, fontSize: 10, height: 22,
              bgcolor: cliente.activo ? alpha('#22c55e', 0.12) : alpha('#94a3b8', 0.12),
              color: cliente.activo ? '#16a34a' : '#64748b',
            }} />
        </Box>

        {/* API Key */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: TEXT_SEC, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>API Key</Typography>
          <Tooltip title="Código que usa la Raspberry Pi para autenticarse al backend. Configúralo en settings.py de la Pi.">
            <InfoOutlined sx={{ fontSize: 13, color: TEXT_SEC, cursor: 'help' }} />
          </Tooltip>
        </Box>
        <ApiKeyBox apiKey={cliente.api_key} />

        <Typography variant="caption" sx={{ color: alpha(TEXT_SEC, 0.7), display: 'block', mt: 1.5 }}>
          Registrado: {cliente.created_at ? new Date(cliente.created_at).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
        </Typography>
      </CardContent>

      <Box sx={{ borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 1, px: 2, py: 1.5 }}>
        <Tooltip title="Editar nombre y logo">
          <IconButton size="small" onClick={() => setEditOpen(true)}
            sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 0.7, color: PRIMARY }}>
            <Edit sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Button size="small" variant="outlined" onClick={toggle} disabled={cargando}
          startIcon={cargando ? <CircularProgress size={12} /> : <PowerSettingsNew />}
          color={cliente.activo ? 'error' : 'success'}
          sx={{ flex: 1, fontSize: 11, borderRadius: 1.5, fontWeight: 600 }}>
          {cliente.activo ? 'Desactivar' : 'Activar'}
        </Button>
        <Tooltip title="Ver usuarios asignados">
          <IconButton size="small" onClick={() => setUsuariosOpen(true)}
            sx={{ border: `1px solid ${alpha('#22c55e', 0.4)}`, borderRadius: 1.5, p: 0.7, color: '#16a34a', '&:hover': { bgcolor: alpha('#22c55e', 0.08) } }}>
            <People sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cámaras ANPR">
          <IconButton size="small" onClick={() => setCamarasOpen(true)}
            sx={{ border: `1px solid ${alpha(BLUE, 0.4)}`, borderRadius: 1.5, p: 0.7, color: BLUE, '&:hover': { bgcolor: alpha(BLUE, 0.08) } }}>
            <Videocam sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        {puedeEliminar && (
          <Tooltip title="Eliminar parqueadero">
            <IconButton size="small" onClick={() => setDeleteOpen(true)}
              sx={{ border: `1px solid ${alpha('#DC2626', 0.35)}`, borderRadius: 1.5, p: 0.7, color: '#DC2626', '&:hover': { bgcolor: alpha('#DC2626', 0.08) } }}>
              <Delete sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <EditarClienteDialog open={editOpen} cliente={cliente} onClose={() => setEditOpen(false)} onGuardado={onActualizar} />
      <CamarasDialog open={camarasOpen} cliente={cliente} onClose={() => setCamarasOpen(false)} />
      <ConfirmDeleteParqueadero
        cliente={cliente}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        deleting={deleting} />

      {/* Diálogo de usuarios del parqueadero */}
      <Dialog open={usuariosOpen} onClose={() => setUsuariosOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', maxHeight: '85vh' } }}>
        <Box sx={{ bgcolor: PRIMARY, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {cliente.logo_url
              ? <Box component="img" src={resolveUrl(cliente.logo_url)} sx={{ width: 30, height: 30, borderRadius: 1, objectFit: 'contain', bgcolor: 'white', p: 0.25 }} />
              : <LocalParking sx={{ color: ORANGE }} />}
            <Box>
              <Typography fontWeight={700} sx={{ color: 'white' }}>Usuarios — {cliente.nombre}</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Usuarios asignados a este parqueadero</Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setUsuariosOpen(false)} sx={{ color: 'rgba(255,255,255,0.6)' }}>
            <Cancel fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 3, bgcolor: '#F4F4F4' }}>
          <UsuariosSistema clientes={clientes || []} filtrarClienteId={String(cliente.id)} />
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Tabla row ────────────────────────────────────────────────────────────────
function ClienteRow({ cliente, onActualizar, puedeEliminar = true }) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const toggle = async () => { await api.toggleActivo(cliente.id, !cliente.activo); onActualizar(); };
  const handleDelete = async (password) => {
    setDeleting(true);
    try {
      await api.eliminar(cliente.id, password);
      setDeleteOpen(false);
      onActualizar();
    } finally {
      setDeleting(false);
    }
  };
  const initials = cliente.nombre.slice(0, 2).toUpperCase();
  return (
    <>
    <TableRow hover sx={{ '& td': { borderColor: BORDER }, '&:hover': { bgcolor: alpha(ORANGE, 0.03) } }}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar src={resolveUrl(cliente.logo_url)} sx={{ width: 36, height: 36, bgcolor: alpha(ORANGE, 0.12), color: ORANGE, fontSize: 12, fontWeight: 700 }}>{initials}</Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ color: PRIMARY }}>{cliente.nombre}</Typography>
            <Typography variant="caption" sx={{ color: TEXT_SEC, fontFamily: 'monospace' }}>/{cliente.slug}</Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell><ApiKeyBox apiKey={cliente.api_key} /></TableCell>
      <TableCell align="center">
        <Chip size="small" label={cliente.activo ? 'Activo' : 'Inactivo'}
          sx={{ fontWeight: 700, bgcolor: cliente.activo ? alpha('#22c55e', 0.12) : alpha('#94a3b8', 0.12), color: cliente.activo ? '#16a34a' : '#64748b' }} />
      </TableCell>
      <TableCell><Typography variant="caption" sx={{ color: TEXT_SEC }}>{cliente.created_at ? new Date(cliente.created_at).toLocaleDateString('es') : '—'}</Typography></TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title={cliente.activo ? 'Desactivar' : 'Activar'}>
            <IconButton size="small" onClick={toggle} color={cliente.activo ? 'error' : 'success'}><PowerSettingsNew fontSize="small" /></IconButton>
          </Tooltip>
          {puedeEliminar && (
            <Tooltip title="Eliminar parqueadero">
              <IconButton size="small" onClick={() => setDeleteOpen(true)} sx={{ color: '#DC2626' }}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
    <ConfirmDeleteParqueadero
      cliente={cliente}
      open={deleteOpen}
      onClose={() => setDeleteOpen(false)}
      onConfirm={handleDelete}
      deleting={deleting} />
    </>
  );
}

// ─── Crear Cliente ────────────────────────────────────────────────────────────
const STEPS = ['Información del parqueadero', 'Credenciales admin', 'Confirmar'];

function CrearClienteDialog({ open, onClose, onCreado }) {
  const empty = { nombre: '', slug: '', admin_email: '', admin_password: '', admin_nombre: '', logo_base64: '', total_parking_spaces: 0 };
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showPass, setShowPass] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resultado, setResultado] = useState(null);

  const reset = () => { setStep(0); setForm(empty); setLogoPreview(null); setError(''); setSuccess(false); setResultado(null); };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => { const n = { ...p, [name]: value }; if (name === 'nombre') n.slug = value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''); return n; });
  };
  const handleLogo = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setLogoPreview(ev.target.result); setForm(p => ({ ...p, logo_base64: ev.target.result })); };
    reader.readAsDataURL(file);
  };
  const canNext = () => step === 0 ? form.nombre.trim().length > 0 : form.admin_email.trim() && form.admin_password.length >= 6;
  const handleSubmit = async () => {
    setCargando(true); setError('');
    try { const r = await api.crear(form); setResultado(r.data.data); setSuccess(true); setStep(3); }
    catch (e) { setError(e.response?.data?.error || 'Error creando cliente'); setStep(2); }
    finally { setCargando(false); }
  };
  const handleClose = () => { if (success) onCreado(); onClose(); setTimeout(reset, 300); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>

      {/* Header naranja */}
      <Box sx={{ bgcolor: PRIMARY, px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box>
          <Typography fontWeight={700} sx={{ color: WHITE, fontSize: 15 }}>{success ? '¡Cliente registrado!' : 'Registrar parqueadero'}</Typography>
          <Typography variant="caption" sx={{ color: alpha(WHITE, 0.6) }}>{success ? resultado?.nombre : STEPS[Math.min(step, 2)]}</Typography>
        </Box>
      </Box>

      {/* Stepper */}
      {!success && (
        <Box sx={{ px: 3, pt: 2, bgcolor: '#fafafa', borderBottom: `1px solid ${BORDER}` }}>
          <Stepper activeStep={step} alternativeLabel sx={{ pb: 2,
            '& .MuiStepLabel-label': { fontSize: 11, color: TEXT_SEC },
            '& .MuiStepLabel-label.Mui-active': { color: PRIMARY, fontWeight: 700 },
            '& .MuiStepIcon-root': { color: BORDER },
            '& .MuiStepIcon-root.Mui-active': { color: ORANGE },
            '& .MuiStepIcon-root.Mui-completed': { color: ORANGE },
            '& .MuiStepConnector-line': { borderColor: BORDER },
          }}>
            {STEPS.map(l => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
          </Stepper>
        </Box>
      )}

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {/* Step 0 */}
        {step === 0 && (
          <Fade in>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: alpha(ORANGE, 0.06), borderRadius: 2, border: `1px dashed ${alpha(ORANGE, 0.3)}` }}>
                <Avatar src={logoPreview} sx={{ width: 64, height: 64, bgcolor: alpha(ORANGE, 0.15), color: ORANGE, fontWeight: 800, fontSize: 22, border: `2px solid ${alpha(ORANGE, 0.3)}` }}>
                  {form.nombre ? form.nombre.slice(0, 2).toUpperCase() : <DirectionsCar />}
                </Avatar>
                <Box>
                  <Button component="label" size="small" variant="outlined" startIcon={<CloudUpload />}
                    sx={{ borderColor: ORANGE, color: ORANGE, borderRadius: 2, fontWeight: 600, '&:hover': { bgcolor: alpha(ORANGE, 0.06) }, mb: 0.5 }}>
                    Subir logo
                    <input type="file" hidden accept="image/*" onChange={handleLogo} />
                  </Button>
                  <Typography variant="caption" sx={{ color: TEXT_SEC, display: 'block' }}>PNG, JPG — máx. 2MB</Typography>
                </Box>
              </Box>
              <TextField fullWidth label="Nombre del parqueadero" name="nombre" value={form.nombre} onChange={handleChange} required autoFocus sx={{ mb: 2 }} />
              <TextField fullWidth label="Slug (identificador)" name="slug" value={form.slug} onChange={handleChange}
                helperText="Se genera automáticamente del nombre" sx={{ mb: 2 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Typography color="text.secondary">/</Typography></InputAdornment> }} />
              <TextField fullWidth label="Número de espacios" name="total_parking_spaces"
                type="number" value={form.total_parking_spaces} onChange={handleChange}
                helperText="Capacidad total del parqueadero"
                slotProps={{ htmlInput: { min: 0 } }} />
            </Box>
          </Fade>
        )}

        {/* Step 1 */}
        {step === 1 && (
          <Fade in>
            <Box>
              <Box sx={{ p: 2, bgcolor: alpha(ORANGE, 0.06), borderRadius: 2, border: `1px solid ${alpha(ORANGE, 0.2)}`, mb: 2.5 }}>
                <Typography variant="body2" sx={{ color: PRIMARY, fontWeight: 600 }}>
                  Administrador de <span style={{ color: ORANGE }}>{form.nombre}</span>
                </Typography>
                <Typography variant="caption" sx={{ color: TEXT_SEC }}>Este usuario gestionará el parqueadero.</Typography>
              </Box>
              <TextField fullWidth label="Nombre del administrador" name="admin_nombre" value={form.admin_nombre} onChange={handleChange} sx={{ mb: 2 }} autoFocus
                InputProps={{ startAdornment: <InputAdornment position="start"><People sx={{ color: 'text.secondary', fontSize: 18 }} /></InputAdornment> }} />
              <TextField fullWidth label="Correo electrónico" name="admin_email" type="email" value={form.admin_email} onChange={handleChange} required sx={{ mb: 2 }} />
              <TextField fullWidth label="Contraseña" name="admin_password" type={showPass ? 'text' : 'password'} value={form.admin_password} onChange={handleChange} required
                helperText="Mínimo 6 caracteres"
                InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPass(v => !v)} size="small">{showPass ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
            </Box>
          </Fade>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <Fade in>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Revisa la información:</Typography>
              <Box sx={{ mt: 1.5, p: 2.5, bgcolor: '#fafafa', borderRadius: 2, border: `1px solid ${BORDER}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar src={logoPreview} sx={{ width: 48, height: 48, bgcolor: alpha(ORANGE, 0.12), color: ORANGE, fontWeight: 800 }}>{form.nombre.slice(0, 2).toUpperCase()}</Avatar>
                  <Box>
                    <Typography fontWeight={700} sx={{ color: PRIMARY }}>{form.nombre}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>/{form.slug}</Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 1.5 }} />
                {[['Administrador', form.admin_email], ['Nombre', form.admin_nombre || '—'], ['Espacios', form.total_parking_spaces || 0]].map(([k, v]) => (
                  <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="caption" color="text.secondary">{k}</Typography>
                    <Typography variant="caption" fontWeight={600} sx={{ color: PRIMARY }}>{v}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Fade>
        )}

        {/* Éxito */}
        {success && resultado && (
          <Fade in>
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Box sx={{ width: 70, height: 70, borderRadius: '50%', bgcolor: alpha('#22c55e', 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                <CheckCircle sx={{ fontSize: 40, color: '#16a34a' }} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: PRIMARY }}>¡Parqueadero registrado!</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>Guarda la API key — no se mostrará de nuevo.</Typography>
              <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, border: `1px solid ${BORDER}` }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>API Key generada</Typography>
                <ApiKeyBox apiKey={resultado.api_key} />
              </Box>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
        {!success ? (
          <>
            <Button onClick={handleClose} sx={{ color: TEXT_SEC, borderRadius: 2 }}>Cancelar</Button>
            {step > 0 && <Button onClick={() => setStep(s => s - 1)} variant="outlined" sx={{ borderRadius: 2 }}>Atrás</Button>}
            {step < 2
              ? <Button variant="contained" onClick={() => { setError(''); setStep(s => s + 1); }} disabled={!canNext()}
                  sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, '&:hover': { bgcolor: '#D97706' }, color: WHITE }}>
                  Siguiente
                </Button>
              : <Button variant="contained" onClick={handleSubmit} disabled={cargando}
                  startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <Add />}
                  sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, minWidth: 140, '&:hover': { bgcolor: '#D97706' }, color: WHITE }}>
                  {cargando ? 'Registrando...' : 'Registrar'}
                </Button>}
          </>
        ) : (
          <Button variant="contained" onClick={handleClose} startIcon={<CheckCircle />}
            sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, '&:hover': { bgcolor: '#D97706' }, color: WHITE }}>
            Listo
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── Sección Usuarios del Sistema ────────────────────────────────────────────
const ROLE_LABELS = { superadmin: 'Super Admin', franquiciado: 'Franquiciado', admin: 'Administrador', supervisor: 'Supervisor', guard: 'Seguridad', operator: 'Operador' };
const ROLE_COLORS = { superadmin: '#7c3aed', franquiciado: '#3b82f6', admin: PRIMARY, supervisor: '#0284c7', guard: '#16a34a', operator: '#D97706' };

// ─── Diálogo crear usuario ────────────────────────────────────────────────────
const ROLES_CREABLES = [
  { value: 'admin',      label: 'Administrador' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'guard',      label: 'Seguridad' },
  { value: 'operator',   label: 'Operador' },
];
const USUARIO_VACIO = { name: '', lastname: '', email: '', password: '', role: 'operator', cliente_id: '' };

function CrearUsuarioDialog({ open, onClose, onCreado, clientes }) {
  const [form, setForm]     = useState(USUARIO_VACIO);
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (open) { setForm(USUARIO_VACIO); setShowPass(false); setError(''); }
  }, [open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim())  { setError('El nombre es obligatorio'); return; }
    if (!form.email.trim()) { setError('El correo es obligatorio'); return; }
    if (form.password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        name:       form.name.trim(),
        lastname:   form.lastname.trim(),
        email:      form.email.trim(),
        username:   form.email.trim().split('@')[0].replace(/\./g, '_').slice(0, 30),
        password:   form.password,
        role:       form.role,
        ...(form.cliente_id ? { cliente_id: Number(form.cliente_id) } : {}),
      };
      const res = await apiClient.post('/api/auth/users', payload);
      if (res.data.success) { onCreado(); onClose(); }
      else setError(res.data.error || 'Error creando usuario');
    } catch (e) {
      setError(e.response?.data?.error || 'Error creando usuario');
    } finally {
      setSaving(false);
    }
  };

  const inp = { size: 'small', sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: PRIMARY, px: 3, py: 2 }}>
        <Typography fontWeight={700} sx={{ color: WHITE }}>Nuevo usuario</Typography>
        <Typography variant="caption" sx={{ color: alpha(WHITE, 0.5) }}>Crea un usuario y asígnalo a un parqueadero</Typography>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField fullWidth label="Nombre *" value={form.name}
            onChange={e => set('name', e.target.value)} autoFocus {...inp} />
          <TextField fullWidth label="Apellido" value={form.lastname}
            onChange={e => set('lastname', e.target.value)} {...inp} />
        </Box>

        <TextField fullWidth label="Correo electrónico *" type="email" value={form.email}
          onChange={e => set('email', e.target.value)} {...inp} />

        <TextField fullWidth label="Contraseña *" value={form.password}
          onChange={e => set('password', e.target.value)}
          type={showPass ? 'text' : 'password'}
          inputProps={{ autoComplete: 'new-password' }}
          InputProps={{ sx: { borderRadius: 2 }, endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setShowPass(v => !v)}>
                {showPass ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
              </IconButton>
            </InputAdornment>
          )}} size="small" />

        <FormControl fullWidth {...inp}>
          <InputLabel>Rol</InputLabel>
          <Select label="Rol" value={form.role} onChange={e => set('role', e.target.value)}>
            {ROLES_CREABLES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl fullWidth {...inp}>
          <InputLabel>Parqueadero (opcional)</InputLabel>
          <Select label="Parqueadero (opcional)" value={form.cliente_id} onChange={e => set('cliente_id', e.target.value)}>
            <MenuItem value=""><em style={{ color: '#aaa' }}>Sin asignar</em></MenuItem>
            {clientes.map(c => (
              <MenuItem key={c.id} value={String(c.id)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {c.logo_url && <Box component="img" src={resolveUrl(c.logo_url)} sx={{ width: 18, height: 18, borderRadius: 0.5, objectFit: 'contain' }} />}
                  {c.nombre}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: TEXT_SEC, borderRadius: 2 }}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Add />}
          sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, color: WHITE, '&:hover': { bgcolor: '#D97706' }, minWidth: 130 }}>
          {saving ? 'Creando...' : 'Crear usuario'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Permisos por usuario (solo superadmin) ───────────────────────────────────
function PermisosUsuarioDialog({ open, usuario, onClose }) {
  const [permisos, setPermisos]   = useState({ corregir_placa: true });
  const [original, setOriginal]   = useState({ corregir_placa: true });
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    if (!open || !usuario) return;
    setError(''); setLoading(true);
    apiClient.get(`/api/auth/users/${usuario.id}/permisos`)
      .then(res => { const p = res.data?.permisos || {}; setPermisos(p); setOriginal(p); })
      .catch(() => setError('No se pudieron cargar los permisos'))
      .finally(() => setLoading(false));
  }, [open, usuario]);

  const guardar = async () => {
    setSaving(true); setError('');
    try {
      const cambios = Object.fromEntries(Object.entries(permisos).filter(([k, v]) => v !== original[k]));
      if (Object.keys(cambios).length) {
        await apiClient.put(`/api/auth/users/${usuario.id}/permisos`, { permisos: cambios });
      }
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudieron guardar los permisos');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Permisos de {usuario?.name} {usuario?.lastname}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box> : (
          <Box>
            <FormControlLabel
              control={<Switch checked={!!permisos.corregir_placa}
                onChange={e => setPermisos(p => ({ ...p, corregir_placa: e.target.checked }))} />}
              label={<Typography variant="body2" fontWeight={600}>Puede corregir placas desde el POS</Typography>} />
            <Typography variant="caption" sx={{ display: 'block', color: '#888' }}>
              Corregir la placa de una entrada con observación (queda en la bitácora). Viene activado por defecto.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={loading || saving}>
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function UsuariosSistema({ clientes, filtrarClienteId = null }) {
  const { user: usuarioActual } = useAuth();
  const soySuperadmin = usuarioActual?.role === 'superadmin';
  const [permisosDialog, setPermisosDialog] = useState(null);
  const [usuarios, setUsuarios]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [busqueda, setBusqueda]             = useState('');
  const [filtroCliente, setFiltroCliente]   = useState(filtrarClienteId || '');
  const [filtroRol, setFiltroRol]           = useState('');
  const [crearOpen, setCrearOpen]           = useState(false);
  const [asignDialog, setAsignDialog]       = useState(null);
  const [asignando, setAsignando]           = useState(false);
  const [selectedClienteIds, setSelectedClienteIds] = useState([]);
  const [busqAsign, setBusqAsign]           = useState('');
  const [verMasDialog, setVerMasDialog]     = useState(null); // { nombre, clienteIds }
  const [page, setPage]                     = useState(0);
  const [rowsPerPage, setRowsPerPage]       = useState(15);

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await apiClient.get('/api/auth/users', { params: { include_inactive: true } });
      setUsuarios(res.data.users || []);
    } catch { setError('Error cargando usuarios'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { setPage(0); }, [busqueda, filtroRol, filtroCliente]);

  const confirmarAsignacion = async () => {
    if (!asignDialog) return;
    setAsignando(true);
    try {
      await apiClient.put(`/api/auth/users/${asignDialog.id}/clientes`, {
        cliente_ids: selectedClienteIds.map(Number),
      });
      await cargar();
      setAsignDialog(null);
    } catch { setError('Error asignando parqueaderos'); }
    finally { setAsignando(false); }
  };

  const eliminarUsuario = async (u) => {
    if (!window.confirm(`¿Eliminar usuario "${u.name} ${u.lastname}"?`)) return;
    try {
      await apiClient.delete(`/api/auth/users/${u.id}`);
      await cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error eliminando usuario'); }
  };

  const toggleClienteId = (id) => {
    const s = String(id);
    setSelectedClienteIds(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    );
  };

  const clienteMap = Object.fromEntries(clientes.map(c => [String(c.id), c]));

  const filtrados = usuarios.filter(u => {
    if (busqueda && !`${u.name} ${u.lastname} ${u.email} ${u.username}`.toLowerCase().includes(busqueda.toLowerCase())) return false;
    if (filtroRol && u.role !== filtroRol) return false;
    if (filtroCliente === '__sin__') return !u.cliente_ids?.length;
    if (filtroCliente) return u.cliente_ids?.includes(Number(filtroCliente));
    return true;
  });

  const paginados = filtrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const ROLE_FILTER_OPTIONS = [
    { value: 'superadmin', label: 'Superadmin' },
    { value: 'franquiciado', label: 'Franquiciado' },
    { value: 'admin', label: 'Administrador' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'guard', label: 'Seguridad' },
    { value: 'operator', label: 'Operador' },
  ];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Toolbar con filtros */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, alignItems: 'center', bgcolor: WHITE, p: 1.5, borderRadius: 2, border: `1px solid ${BORDER}`, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Buscar usuario..." value={busqueda} onChange={e => setBusqueda(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: TEXT_SEC }} /></InputAdornment>, sx: { borderRadius: 2, bgcolor: BG, '& fieldset': { borderColor: BORDER } } }}
          sx={{ flex: 1, minWidth: 180 }} />

        {/* Filtro por rol */}
        <FormControl size="small" sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: BG, '& fieldset': { borderColor: BORDER } } }}>
          <Select value={filtroRol} onChange={e => setFiltroRol(e.target.value)} displayEmpty>
            <MenuItem value="">Todos los roles</MenuItem>
            {ROLE_FILTER_OPTIONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </Select>
        </FormControl>

        {/* Filtro por parqueadero */}
        <FormControl size="small" sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: BG, '& fieldset': { borderColor: BORDER } } }}>
          <Select value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)} displayEmpty>
            <MenuItem value="">Todos los parqueaderos</MenuItem>
            <MenuItem value="__sin__"><em style={{ color: '#aaa' }}>Sin asignar</em></MenuItem>
            {clientes.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.nombre}</MenuItem>)}
          </Select>
        </FormControl>

        <Tooltip title="Actualizar">
          <IconButton onClick={cargar} disabled={loading} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>

        <Button variant="contained" startIcon={<Add />} onClick={() => setCrearOpen(true)}
          sx={{ bgcolor: ORANGE, color: WHITE, borderRadius: 2, fontWeight: 700, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#D97706' } }}>
          Nuevo usuario
        </Button>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Total', value: usuarios.length, color: PRIMARY },
          { label: 'Sin parqueadero', value: usuarios.filter(u => !u.cliente_ids?.length).length, color: '#F59E0B' },
          { label: 'Con parqueadero', value: usuarios.filter(u => u.cliente_ids?.length > 0).length, color: '#22c55e' },
          { label: 'Mostrando', value: filtrados.length, color: '#3b82f6' },
        ].map(s => (
          <Box key={s.label} sx={{ bgcolor: WHITE, borderRadius: 2, px: 2.5, py: 1.5, border: `1px solid ${BORDER}`, textAlign: 'center', minWidth: 90 }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
            <Typography variant="caption" sx={{ color: TEXT_SEC, fontWeight: 600 }}>{s.label}</Typography>
          </Box>
        ))}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ORANGE }} /></Box>
      ) : (
        <Box sx={{ bgcolor: WHITE, borderRadius: 3, border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: BG }}>
              <TableRow>
                {['Usuario', 'Rol / Email', 'Parqueaderos', 'Acciones'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: 0.5, borderColor: BORDER }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginados.map(u => {
                const color = ROLE_COLORS[u.role] || PRIMARY;
                const clienteIds = u.cliente_ids || [];
                const MAX_CHIPS = 3;
                const visibles = clienteIds.slice(0, MAX_CHIPS);
                const extras   = clienteIds.length - MAX_CHIPS;
                return (
                  <TableRow key={u.id} hover sx={{ '& td': { borderColor: '#F0F0F0' } }}>
                    {/* Usuario */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(color, 0.15), color, fontSize: 13, fontWeight: 700 }}>
                          {u.name?.[0]}{u.lastname?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ color: PRIMARY }}>{u.name} {u.lastname}</Typography>
                          <Typography variant="caption" sx={{ color: TEXT_SEC, fontFamily: 'monospace' }}>@{u.username}</Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Rol + Email */}
                    <TableCell>
                      <Chip size="small" label={ROLE_LABELS[u.role] || u.role}
                        sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: alpha(color, 0.1), color, mb: 0.5, display: 'block', width: 'fit-content' }} />
                      <Typography variant="caption" sx={{ color: TEXT_SEC }}>{u.email}</Typography>
                    </TableCell>

                    {/* Parqueaderos (máx 3 chips + "+N ver más") */}
                    <TableCell>
                      {clienteIds.length === 0 ? (
                        <Chip size="small" label="Sin asignar"
                          sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: alpha('#F59E0B', 0.1), color: '#B45309' }} />
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                          {visibles.map(cid => {
                            const c = clienteMap[String(cid)];
                            return c ? (
                              <Chip key={cid} size="small"
                                avatar={c.logo_url ? <Avatar src={resolveUrl(c.logo_url)} sx={{ width: 16, height: 16 }} /> : undefined}
                                label={c.nombre}
                                sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: alpha(ORANGE, 0.1), color: '#B45309' }} />
                            ) : null;
                          })}
                          {extras > 0 && (
                            <Chip size="small" label={`+${extras} más`}
                              onClick={() => setVerMasDialog({ nombre: `${u.name} ${u.lastname}`, clienteIds })}
                              sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: alpha(PRIMARY, 0.08), color: PRIMARY, cursor: 'pointer', '&:hover': { bgcolor: alpha(PRIMARY, 0.15) } }} />
                          )}
                        </Box>
                      )}
                    </TableCell>

                    {/* Acciones */}
                    <TableCell>
                      {u.role === 'superadmin' ? (
                        <Chip size="small" label="Protegido" sx={{ height: 20, fontSize: 10, bgcolor: alpha('#7c3aed', 0.1), color: '#7c3aed' }} />
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {soySuperadmin && (
                            <Tooltip title="Permisos del usuario">
                              <IconButton size="small" onClick={() => setPermisosDialog(u)}
                                sx={{ border: '1px solid #E5E5E5', borderRadius: 1.5, color: ORANGE }}>
                                <VerifiedUser sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Asignar parqueaderos">
                            <IconButton size="small"
                              onClick={() => {
                                setAsignDialog(u);
                                setSelectedClienteIds((u.cliente_ids || []).map(String));
                              }}
                              sx={{ border: '1px solid #E5E5E5', borderRadius: 1.5, color: ORANGE }}>
                              <LocalParking sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar usuario">
                            <IconButton size="small" color="error" onClick={() => eliminarUsuario(u)}
                              sx={{ border: '1px solid', borderColor: '#fecaca', borderRadius: 1.5 }}>
                              <Delete sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {filtrados.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6, color: TEXT_SEC }}>
              <ManageAccounts sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
              <Typography variant="body2">Sin usuarios encontrados</Typography>
            </Box>
          )}
          {filtrados.length > 0 && (
            <TablePagination
              component="div"
              count={filtrados.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 15, 25, 50]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{ borderTop: `1px solid ${BORDER}`, '& .MuiTablePagination-toolbar': { fontSize: 12 } }}
            />
          )}
        </Box>
      )}

      {/* Diálogo ver más parqueaderos */}
      <Dialog open={!!verMasDialog} onClose={() => setVerMasDialog(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ bgcolor: PRIMARY, px: 3, py: 2 }}>
          <Typography fontWeight={700} sx={{ color: 'white' }}>Parqueaderos asignados</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{verMasDialog?.nombre}</Typography>
        </Box>
        <DialogContent sx={{ px: 2, py: 1.5, maxHeight: 360, overflowY: 'auto' }}>
          {verMasDialog?.clienteIds.map(cid => {
            const c = clienteMap[String(cid)];
            return c ? (
              <Box key={cid} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: `1px solid ${BORDER}` }}>
                {c.logo_url ? (
                  <Box component="img" src={resolveUrl(c.logo_url)} sx={{ width: 28, height: 28, borderRadius: 1, objectFit: 'contain', bgcolor: '#f8f8f8', p: '2px', flexShrink: 0 }} />
                ) : (
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: alpha(ORANGE, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <LocalParking sx={{ fontSize: 16, color: ORANGE }} />
                  </Box>
                )}
                <Typography variant="body2" sx={{ color: PRIMARY, fontWeight: 500 }}>{c.nombre}</Typography>
              </Box>
            ) : null;
          })}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setVerMasDialog(null)} variant="contained"
            sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, color: WHITE, '&:hover': { bgcolor: '#D97706' } }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <CrearUsuarioDialog
        open={crearOpen}
        onClose={() => setCrearOpen(false)}
        onCreado={cargar}
        clientes={clientes} />

      <PermisosUsuarioDialog
        open={!!permisosDialog}
        usuario={permisosDialog}
        onClose={() => setPermisosDialog(null)} />

      {/* Diálogo asignar parqueaderos (multi-select con checkboxes, filtro y seleccionar todo) */}
      {(() => {
        const clientesFiltrados = clientes.filter(c =>
          c.nombre.toLowerCase().includes(busqAsign.toLowerCase())
        );
        const todosMarcados = clientesFiltrados.length > 0 &&
          clientesFiltrados.every(c => selectedClienteIds.includes(String(c.id)));
        const algunoMarcado = clientesFiltrados.some(c => selectedClienteIds.includes(String(c.id)));
        const toggleTodos = () => {
          if (todosMarcados) {
            setSelectedClienteIds(prev => prev.filter(id => !clientesFiltrados.some(c => String(c.id) === id)));
          } else {
            const nuevos = clientesFiltrados.map(c => String(c.id));
            setSelectedClienteIds(prev => [...new Set([...prev, ...nuevos])]);
          }
        };
        const cerrar = () => { setAsignDialog(null); setBusqAsign(''); };
        return (
          <Dialog open={!!asignDialog} onClose={cerrar} maxWidth="xs" fullWidth
            PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
            <Box sx={{ bgcolor: PRIMARY, px: 3, py: 2 }}>
              <Typography fontWeight={700} sx={{ color: 'white' }}>Asignar parqueaderos</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                {asignDialog?.name} {asignDialog?.lastname}
              </Typography>
            </Box>

            <Box sx={{ px: 3, pt: 2, pb: 1 }}>
              <TextField size="small" fullWidth placeholder="Buscar parqueadero..."
                value={busqAsign} onChange={e => setBusqAsign(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: TEXT_SEC }} /></InputAdornment>,
                  sx: { borderRadius: 2, bgcolor: BG, '& fieldset': { borderColor: BORDER } },
                }} />
            </Box>

            {clientes.length > 0 && (
              <Box sx={{ px: 3, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={toggleTodos}>
                  <Checkbox size="small" checked={todosMarcados} indeterminate={algunoMarcado && !todosMarcados}
                    onChange={toggleTodos} onClick={e => e.stopPropagation()}
                    sx={{ p: 0.5, color: TEXT_SEC, '&.Mui-checked': { color: ORANGE }, '&.MuiCheckbox-indeterminate': { color: ORANGE } }} />
                  <Typography variant="caption" sx={{ color: TEXT_SEC }}>
                    {todosMarcados ? 'Deseleccionar todo' : 'Seleccionar todo'}{busqAsign ? ' (filtrados)' : ''}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: TEXT_SEC }}>
                  {selectedClienteIds.length} seleccionado{selectedClienteIds.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}

            <DialogContent sx={{ pt: 0.5, px: 3, pb: 1, maxHeight: 320, overflowY: 'auto' }}>
              {clientes.length === 0 ? (
                <Typography variant="body2" sx={{ color: TEXT_SEC, py: 1 }}>No hay parqueaderos registrados</Typography>
              ) : clientesFiltrados.length === 0 ? (
                <Typography variant="body2" sx={{ color: TEXT_SEC, py: 2, textAlign: 'center' }}>Sin resultados</Typography>
              ) : clientesFiltrados.map(c => (
                <Box key={c.id} onClick={() => toggleClienteId(c.id)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: `1px solid ${BORDER}`, cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: alpha(ORANGE, 0.04) } }}>
                  <Checkbox size="small" checked={selectedClienteIds.includes(String(c.id))}
                    onChange={() => toggleClienteId(c.id)} onClick={e => e.stopPropagation()}
                    sx={{ p: 0.5, color: TEXT_SEC, '&.Mui-checked': { color: ORANGE } }} />
                  {c.logo_url ? (
                    <Box component="img" src={resolveUrl(c.logo_url)} sx={{ width: 22, height: 22, borderRadius: 1, objectFit: 'contain', bgcolor: '#f8f8f8', p: '1px', flexShrink: 0 }} />
                  ) : (
                    <Box sx={{ width: 22, height: 22, borderRadius: 1, bgcolor: alpha(ORANGE, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <LocalParking sx={{ fontSize: 13, color: ORANGE }} />
                    </Box>
                  )}
                  <Typography variant="body2" sx={{ color: PRIMARY }}>{c.nombre}</Typography>
                </Box>
              ))}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
              <Button onClick={cerrar} sx={{ color: '#888', borderRadius: 2 }}>Cancelar</Button>
              <Button variant="contained" onClick={confirmarAsignacion} disabled={asignando}
                startIcon={asignando ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, color: 'white', '&:hover': { bgcolor: '#D97706' } }}>
                {asignando ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </Box>
  );
}

// ─── Franquiciados (solo los gestiona el superadmin) ────────────────────────
function SelectorParqueaderos({ ids, onIds, clientes }) {
  const toggle = (id) => onIds(ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  return (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, py: 0.75, bgcolor: BG, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="caption" sx={{ color: TEXT_SEC, fontWeight: 600 }}>
          Parqueaderos que podrá administrar (también verá los que cree)
        </Typography>
        <Typography variant="caption" sx={{ color: TEXT_SEC, whiteSpace: 'nowrap' }}>
          {ids.length} seleccionado{ids.length !== 1 ? 's' : ''}
        </Typography>
      </Box>
      <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
        {clientes.length === 0 && (
          <Typography variant="body2" sx={{ color: TEXT_SEC, p: 2 }}>No hay parqueaderos registrados</Typography>
        )}
        {clientes.map(c => (
          <Box key={c.id} onClick={() => toggle(c.id)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.25, cursor: 'pointer',
              borderTop: `1px solid ${BORDER}`, '&:hover': { bgcolor: alpha(ORANGE, 0.05) } }}>
            <Checkbox size="small" checked={ids.includes(c.id)} onClick={e => e.stopPropagation()}
              onChange={() => toggle(c.id)} sx={{ p: 0.5, '&.Mui-checked': { color: ORANGE } }} />
            <Typography variant="body2" sx={{ color: PRIMARY }}>{c.nombre}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const FRANQUICIADO_VACIO = { name: '', lastname: '', email: '', password: '' };

function CrearFranquiciadoDialog({ open, onClose, onCreado, clientes }) {
  const [form, setForm]         = useState(FRANQUICIADO_VACIO);
  const [ids, setIds]           = useState([]);
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (open) { setForm(FRANQUICIADO_VACIO); setIds([]); setShowPass(false); setError(''); }
  }, [open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const guardar = async () => {
    if (!form.name.trim())  { setError('El nombre es obligatorio'); return; }
    if (!form.email.trim()) { setError('El correo es obligatorio'); return; }
    if (form.password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
    setSaving(true); setError('');
    try {
      await apiClient.post('/api/auth/franquiciados', {
        name: form.name.trim(), lastname: form.lastname.trim(), email: form.email.trim(),
        password: form.password, cliente_ids: ids,
      });
      onCreado(); onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Error creando el franquiciado');
    } finally { setSaving(false); }
  };

  const inp = { size: 'small', sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: PRIMARY, px: 3, py: 2 }}>
        <Typography fontWeight={700} sx={{ color: WHITE }}>Nuevo franquiciado</Typography>
        <Typography variant="caption" sx={{ color: alpha(WHITE, 0.5) }}>Solo verá los parqueaderos que le asignes o cree</Typography>
      </Box>
      <DialogContent sx={{ px: 3, pt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField fullWidth label="Nombre *" value={form.name} onChange={e => set('name', e.target.value)} autoFocus {...inp} />
          <TextField fullWidth label="Apellido" value={form.lastname} onChange={e => set('lastname', e.target.value)} {...inp} />
        </Box>
        <TextField fullWidth label="Correo electrónico *" type="email" value={form.email}
          onChange={e => set('email', e.target.value)} {...inp} />
        <TextField fullWidth label="Contraseña *" value={form.password}
          onChange={e => set('password', e.target.value)}
          type={showPass ? 'text' : 'password'}
          inputProps={{ autoComplete: 'new-password' }}
          InputProps={{ sx: { borderRadius: 2 }, endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setShowPass(v => !v)}>
                {showPass ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
              </IconButton>
            </InputAdornment>
          )}} size="small" />
        <SelectorParqueaderos ids={ids} onIds={setIds} clientes={clientes} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: TEXT_SEC, borderRadius: 2 }}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Add />}
          sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, color: WHITE, '&:hover': { bgcolor: '#D97706' }, minWidth: 150 }}>
          {saving ? 'Creando...' : 'Crear franquiciado'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ParqueaderosFranquiciadoDialog({ open, usuario, onClose, onGuardado, clientes }) {
  const [ids, setIds]       = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (open && usuario) { setIds(usuario.cliente_ids || []); setError(''); }
  }, [open, usuario]);

  const guardar = async () => {
    setSaving(true); setError('');
    try {
      await apiClient.put(`/api/auth/franquiciados/${usuario.id}/clientes`, { cliente_ids: ids });
      onGuardado(); onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Error guardando los parqueaderos');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: PRIMARY, px: 3, py: 2 }}>
        <Typography fontWeight={700} sx={{ color: WHITE }}>Parqueaderos del franquiciado</Typography>
        <Typography variant="caption" sx={{ color: alpha(WHITE, 0.5) }}>{usuario?.name} {usuario?.lastname}</Typography>
      </Box>
      <DialogContent sx={{ px: 3, pt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
        <SelectorParqueaderos ids={ids} onIds={setIds} clientes={clientes} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: TEXT_SEC, borderRadius: 2 }}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, color: WHITE, '&:hover': { bgcolor: '#D97706' } }}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditarFranquiciadoDialog({ open, usuario, onClose, onGuardado }) {
  const [form, setForm]         = useState({ name: '', lastname: '', email: '', is_active: true, password: '' });
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (open && usuario) {
      setForm({
        name: usuario.name || '', lastname: usuario.lastname === '-' ? '' : (usuario.lastname || ''),
        email: usuario.email || '', is_active: usuario.is_active !== false, password: '',
      });
      setShowPass(false); setError('');
    }
  }, [open, usuario]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const guardar = async () => {
    if (form.name.trim().length < 2) { setError('El nombre debe tener al menos 2 caracteres'); return; }
    if (!form.email.trim()) { setError('El correo es obligatorio'); return; }
    if (form.password && form.password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
    setSaving(true); setError('');
    try {
      const datos = { name: form.name.trim(), email: form.email.trim(), is_active: form.is_active };
      if (form.lastname.trim().length >= 2) datos.lastname = form.lastname.trim();
      await apiClient.put(`/api/auth/users/${usuario.id}`, datos);
      if (form.password) await apiClient.put(`/api/auth/users/${usuario.id}/password`, { password: form.password });
      onGuardado(); onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Error guardando los cambios');
    } finally { setSaving(false); }
  };

  const inp = { size: 'small', sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: PRIMARY, px: 3, py: 2 }}>
        <Typography fontWeight={700} sx={{ color: WHITE }}>Editar franquiciado</Typography>
        <Typography variant="caption" sx={{ color: alpha(WHITE, 0.5) }}>@{usuario?.username}</Typography>
      </Box>
      <DialogContent sx={{ px: 3, pt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField fullWidth label="Nombre *" value={form.name} onChange={e => set('name', e.target.value)} {...inp} />
          <TextField fullWidth label="Apellido" value={form.lastname} onChange={e => set('lastname', e.target.value)} {...inp} />
        </Box>
        <TextField fullWidth label="Correo electrónico *" type="email" value={form.email}
          onChange={e => set('email', e.target.value)} {...inp} />
        <TextField fullWidth label="Nueva contraseña (opcional)" value={form.password}
          onChange={e => set('password', e.target.value)}
          type={showPass ? 'text' : 'password'}
          inputProps={{ autoComplete: 'new-password' }}
          helperText="Déjala vacía para no cambiarla"
          InputProps={{ sx: { borderRadius: 2 }, endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setShowPass(v => !v)}>
                {showPass ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
              </IconButton>
            </InputAdornment>
          )}} size="small" />
        <FormControlLabel
          control={<Switch checked={form.is_active} onChange={e => set('is_active', e.target.checked)}
            sx={{ '& .Mui-checked': { color: ORANGE }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: ORANGE } }} />}
          label={<Typography variant="body2" fontWeight={600}>{form.is_active ? 'Cuenta activa' : 'Cuenta desactivada (no puede iniciar sesión)'}</Typography>} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: TEXT_SEC, borderRadius: 2 }}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, color: WHITE, '&:hover': { bgcolor: '#D97706' } }}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FranquiciadosSistema({ clientes }) {
  const [usuarios, setUsuarios]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [crearOpen, setCrearOpen] = useState(false);
  const [editando, setEditando]   = useState(null);
  const [datosEdit, setDatosEdit]   = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await apiClient.get('/api/auth/franquiciados');
      setUsuarios(res.data.users || []);
    } catch (e) { setError(e.response?.data?.error || 'Error cargando franquiciados'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const eliminar = async (u) => {
    if (!window.confirm(`¿Eliminar al franquiciado "${u.name} ${u.lastname}"? Sus parqueaderos no se borran.`)) return;
    try {
      await apiClient.delete(`/api/auth/franquiciados/${u.id}`);
      await cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error eliminando franquiciado'); }
  };

  const clienteMap = Object.fromEntries(clientes.map(c => [c.id, c]));

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, alignItems: 'center', bgcolor: WHITE, p: 1.5, borderRadius: 2, border: `1px solid ${BORDER}`, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: TEXT_SEC, flex: 1, minWidth: 220 }}>
          Un franquiciado solo ve y administra los parqueaderos que le asignes y los que cree él mismo. No puede eliminar parqueaderos ni gestionar superadmins.
        </Typography>
        <Tooltip title="Actualizar">
          <IconButton onClick={cargar} disabled={loading} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCrearOpen(true)}
          sx={{ bgcolor: ORANGE, color: WHITE, borderRadius: 2, fontWeight: 700, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#D97706' } }}>
          Nuevo franquiciado
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ORANGE }} /></Box>
      ) : (
        <Box sx={{ bgcolor: WHITE, borderRadius: 3, border: `1px solid ${BORDER}`, overflow: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: BG }}>
              <TableRow>
                {['Franquiciado', 'Correo', 'Parqueaderos', 'Estado', 'Acciones'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: 0.5, borderColor: BORDER }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {usuarios.map(u => {
                const asignados = u.cliente_ids || [];
                return (
                  <TableRow key={u.id} hover sx={{ '& td': { borderColor: '#F0F0F0' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(BLUE, 0.15), color: BLUE, fontSize: 13, fontWeight: 700 }}>
                          {u.name?.[0]}{u.lastname?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ color: PRIMARY }}>{u.name} {u.lastname}</Typography>
                          <Typography variant="caption" sx={{ color: TEXT_SEC, fontFamily: 'monospace' }}>@{u.username}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="caption" sx={{ color: TEXT_SEC }}>{u.email}</Typography></TableCell>
                    <TableCell>
                      {asignados.length === 0 ? (
                        <Chip size="small" label="Ninguno aún"
                          sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: alpha('#F59E0B', 0.1), color: '#B45309' }} />
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {asignados.slice(0, 3).map(cid => (
                            <Chip key={cid} size="small" label={clienteMap[cid]?.nombre || `#${cid}`}
                              sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: alpha(ORANGE, 0.1), color: '#B45309' }} />
                          ))}
                          {asignados.length > 3 && (
                            <Chip size="small" label={`+${asignados.length - 3} más`}
                              sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: alpha(PRIMARY, 0.08), color: PRIMARY }} />
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={u.is_active === false ? 'Desactivado' : 'Activo'}
                        sx={{ height: 20, fontSize: 10, fontWeight: 700,
                          bgcolor: alpha(u.is_active === false ? '#DC2626' : GREEN, 0.12),
                          color: u.is_active === false ? '#B91C1C' : '#15803d' }} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Editar datos, clave o desactivar">
                          <IconButton size="small" onClick={() => setDatosEdit(u)}
                            sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, color: BLUE }}>
                            <Edit sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Asignar parqueaderos">
                          <IconButton size="small" onClick={() => setEditando(u)}
                            sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, color: ORANGE }}>
                            <LocalParking sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar franquiciado">
                          <IconButton size="small" color="error" onClick={() => eliminar(u)}
                            sx={{ border: '1px solid', borderColor: '#fecaca', borderRadius: 1.5 }}>
                            <Delete sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {usuarios.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6, color: TEXT_SEC }}>
              <AdminPanelSettings sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
              <Typography variant="body2">Aún no hay franquiciados</Typography>
            </Box>
          )}
        </Box>
      )}

      <CrearFranquiciadoDialog open={crearOpen} onClose={() => setCrearOpen(false)} onCreado={cargar} clientes={clientes} />
      <EditarFranquiciadoDialog open={!!datosEdit} usuario={datosEdit} onClose={() => setDatosEdit(null)} onGuardado={cargar} />
      <ParqueaderosFranquiciadoDialog open={!!editando} usuario={editando} onClose={() => setEditando(null)}
        onGuardado={cargar} clientes={clientes} />
    </Box>
  );
}

// ─── Dispositivos POS (IMEI) ────────────────────────────────────────────────
const apiDispositivos = {
  listar:      () => apiClient.get('/api/dispositivos'),
  crear:       (d) => apiClient.post('/api/dispositivos', d),
  actualizar:  (id, d) => apiClient.put(`/api/dispositivos/${id}`, d),
  eliminar:    (id) => apiClient.delete(`/api/dispositivos/${id}`),
};

const TIPO_COBRO_LABELS = { prepago: 'Prepago', postpago: 'Postpago', ambos: 'Ambos' };

function CrearDispositivoDialog({ open, onClose, onCreado, clientes }) {
  const vacio = { imei: '', nombre: '', cliente_id: '', tipo_cobro: 'prepago' };
  const [form, setForm] = useState(vacio);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setForm(vacio); setError(''); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const guardar = async () => {
    if (!form.imei.trim()) { setError('El IMEI es requerido'); return; }
    setGuardando(true); setError('');
    try {
      await apiDispositivos.crear({
        imei: form.imei.trim(),
        nombre: form.nombre.trim() || null,
        cliente_id: form.cliente_id ? Number(form.cliente_id) : null,
        tipo_cobro: form.tipo_cobro,
      });
      onCreado();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Error registrando el dispositivo');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: PRIMARY, px: 3, py: 2.5 }}>
        <Typography fontWeight={700} sx={{ color: WHITE, fontSize: 15 }}>Registrar dispositivo POS</Typography>
        <Typography variant="caption" sx={{ color: alpha(WHITE, 0.6) }}>El IMEI que mande este POS en su login</Typography>
      </Box>
      <DialogContent sx={{ px: 3, pt: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        <TextField fullWidth label="IMEI *" value={form.imei} autoFocus
          onChange={e => setForm(p => ({ ...p, imei: e.target.value }))}
          placeholder="351234567891234" sx={{ mb: 2 }} />
        <TextField fullWidth label="Nombre (opcional)" value={form.nombre}
          onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))}
          placeholder="Ej: POS Garita 1" sx={{ mb: 2 }} />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Parqueadero (opcional)</InputLabel>
          <Select value={form.cliente_id} label="Parqueadero (opcional)"
            onChange={e => setForm(p => ({ ...p, cliente_id: e.target.value }))}>
            <MenuItem value=""><em style={{ color: '#aaa' }}>Sin asignar — asignar después</em></MenuItem>
            {clientes.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.nombre}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Tipo de cobro</InputLabel>
          <Select value={form.tipo_cobro} label="Tipo de cobro"
            onChange={e => setForm(p => ({ ...p, tipo_cobro: e.target.value }))}>
            {Object.entries(TIPO_COBRO_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: TEXT_SEC, borderRadius: 2 }}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={guardando}
          startIcon={guardando ? <CircularProgress size={14} color="inherit" /> : <Add />}
          sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, color: WHITE, '&:hover': { bgcolor: '#D97706' } }}>
          {guardando ? 'Registrando...' : 'Registrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DispositivosPos({ clientes }) {
  const [dispositivos, setDispositivos] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [busqueda, setBusqueda]         = useState('');
  const [crearOpen, setCrearOpen]       = useState(false);
  const [guardandoId, setGuardandoId]   = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await apiDispositivos.listar();
      setDispositivos(res.data.data || []);
    } catch { setError('Error cargando dispositivos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const asignarCliente = async (d, clienteId) => {
    setGuardandoId(d.id); setError('');
    try {
      await apiDispositivos.actualizar(d.id, { cliente_id: clienteId ? Number(clienteId) : null });
      await cargar();
    } catch { setError('Error asignando el parqueadero'); }
    finally { setGuardandoId(null); }
  };

  const asignarTipoCobro = async (d, tipoCobro) => {
    setGuardandoId(d.id); setError('');
    try {
      await apiDispositivos.actualizar(d.id, { tipo_cobro: tipoCobro });
      await cargar();
    } catch { setError('Error actualizando el tipo de pago'); }
    finally { setGuardandoId(null); }
  };

  const toggleActivo = async (d) => {
    setGuardandoId(d.id); setError('');
    try {
      await apiDispositivos.actualizar(d.id, { activo: !d.activo });
      await cargar();
    } catch { setError('Error actualizando el estado'); }
    finally { setGuardandoId(null); }
  };

  const eliminar = async (d) => {
    if (!window.confirm(`¿Eliminar el dispositivo "${d.nombre || d.imei}"? Dejará de poder iniciar sesión.`)) return;
    try {
      await apiDispositivos.eliminar(d.id);
      await cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error eliminando el dispositivo'); }
  };

  const filtrados = dispositivos.filter(d =>
    !busqueda || `${d.imei} ${d.nombre || ''}`.toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, alignItems: 'center', bgcolor: WHITE, p: 1.5, borderRadius: 2, border: `1px solid ${BORDER}`, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Buscar por IMEI o nombre..." value={busqueda} onChange={e => setBusqueda(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: TEXT_SEC }} /></InputAdornment>, sx: { borderRadius: 2, bgcolor: BG, '& fieldset': { borderColor: BORDER } } }}
          sx={{ flex: 1, minWidth: 200 }} />

        <Tooltip title="Actualizar">
          <IconButton onClick={cargar} disabled={loading} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>

        <Button variant="contained" startIcon={<Add />} onClick={() => setCrearOpen(true)}
          sx={{ bgcolor: ORANGE, color: WHITE, borderRadius: 2, fontWeight: 700, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#D97706' } }}>
          Nuevo dispositivo
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Total', value: dispositivos.length, color: PRIMARY },
          { label: 'Sin asignar', value: dispositivos.filter(d => !d.cliente_id).length, color: '#F59E0B' },
          { label: 'Activos', value: dispositivos.filter(d => d.activo).length, color: '#22c55e' },
          { label: 'Inactivos', value: dispositivos.filter(d => !d.activo).length, color: '#ef4444' },
        ].map(s => (
          <Box key={s.label} sx={{ bgcolor: WHITE, borderRadius: 2, px: 2.5, py: 1.5, border: `1px solid ${BORDER}`, textAlign: 'center', minWidth: 90 }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
            <Typography variant="caption" sx={{ color: TEXT_SEC, fontWeight: 600 }}>{s.label}</Typography>
          </Box>
        ))}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ORANGE }} /></Box>
      ) : (
        <Box sx={{ bgcolor: WHITE, borderRadius: 3, border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: BG }}>
              <TableRow>
                {['Dispositivo', 'IMEI', 'Parqueadero', 'Tipo de cobro', 'Estado', 'Acciones'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: 0.5, borderColor: BORDER }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtrados.map(d => (
                <TableRow key={d.id} hover sx={{ '& td': { borderColor: '#F0F0F0' }, opacity: d.activo ? 1 : 0.6 }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(ORANGE, 0.15), color: ORANGE }}>
                        <PhoneIphone sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Typography variant="body2" fontWeight={600} sx={{ color: PRIMARY }}>{d.nombre || '—'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: TEXT_SEC }}>{d.imei}</Typography>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 } }}>
                      <Select value={d.cliente_id ? String(d.cliente_id) : ''} displayEmpty
                        disabled={guardandoId === d.id}
                        onChange={e => asignarCliente(d, e.target.value)}>
                        <MenuItem value=""><em style={{ color: '#aaa' }}>Sin asignar</em></MenuItem>
                        {clientes.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.nombre}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 } }}>
                      <Select value={d.tipo_cobro || 'prepago'}
                        disabled={guardandoId === d.id}
                        onChange={e => asignarTipoCobro(d, e.target.value)}>
                        {Object.entries(TIPO_COBRO_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={<Switch size="small" checked={d.activo} disabled={guardandoId === d.id}
                        onChange={() => toggleActivo(d)} />}
                      label={<Typography variant="caption" sx={{ color: d.activo ? '#16a34a' : TEXT_SEC, fontWeight: 600 }}>
                        {d.activo ? 'Activo' : 'Inactivo'}
                      </Typography>}
                      sx={{ ml: 0 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => eliminar(d)}
                        sx={{ border: '1px solid', borderColor: '#fecaca', borderRadius: 1.5 }}>
                        <Delete sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filtrados.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6, color: TEXT_SEC }}>
              <PhoneIphone sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
              <Typography variant="body2">Sin dispositivos registrados</Typography>
            </Box>
          )}
        </Box>
      )}

      <CrearDispositivoDialog open={crearOpen} onClose={() => setCrearOpen(false)} onCreado={cargar} clientes={clientes} />
    </Box>
  );
}

// ─── Panel Principal ──────────────────────────────────────────────────────────
export default function SuperAdminPanel() {
  const { user, logout } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [tab, setTab] = useState(0);
  const [vista, setVista] = useState('grid');
  const esFranquiciado = user?.role === 'franquiciado';

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try { const r = await api.listar(); setClientes(r.data.data || []); }
    catch { setError('No se pudieron cargar los clientes'); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = clientes.filter(c =>
    c.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    c.slug.toLowerCase().includes(busqueda.toLowerCase())
  );
  const activos = clientes.filter(c => c.activo).length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BG }}>

      {/* ── AppBar ── */}
      <Box sx={{ bgcolor: PRIMARY, px: 3, height: 64, display: 'flex', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box component="img" src="/ParkeyLogoWhite.svg" alt="Parkey" sx={{ height: 77, objectFit: 'contain' }} />
          <Typography variant="caption" sx={{ color: alpha(WHITE, 0.55), fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 600 }}>
            Super Admin
          </Typography>
          {esFranquiciado && (
            <Chip size="small" label={`Franquiciado · ${clientes.length} parqueadero${clientes.length !== 1 ? 's' : ''}`}
              sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: alpha(ORANGE, 0.2), color: ORANGE }} />
          )}
        </Box>

        <Box sx={{ flex: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 34, height: 34, bgcolor: ORANGE, color: WHITE, fontSize: 13, fontWeight: 700 }}>
            {user?.name?.[0] || user?.email?.[0] || 'S'}
          </Avatar>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="caption" sx={{ color: WHITE, fontWeight: 600, display: 'block', lineHeight: 1.3 }}>{user?.name || 'Superadmin'}</Typography>
            <Typography variant="caption" sx={{ color: alpha(WHITE, 0.5), fontSize: 10 }}>{user?.email}</Typography>
          </Box>
          <Tooltip title="Cerrar sesión">
            <IconButton onClick={logout} size="small" sx={{ color: alpha(WHITE, 0.6), '&:hover': { color: '#f87171' }, ml: 0.5 }}>
              <Logout fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Contenido ── */}
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>

        {/* ── Tabs de navegación ── */}
        <Box sx={{ borderBottom: `1px solid ${BORDER}`, mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}
            sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, color: TEXT_SEC, minHeight: 48, gap: 1 },
                 '& .Mui-selected': { color: PRIMARY },
                 '& .MuiTabs-indicator': { bgcolor: ORANGE, height: 3, borderRadius: 2 } }}>
            <Tab label="Parqueaderos" icon={<LocalParking sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Usuarios del sistema" icon={<ManageAccounts sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Dispositivos POS" icon={<PhoneIphone sx={{ fontSize: 18 }} />} iconPosition="start" />
            {!esFranquiciado && <Tab label="Franquiciados" icon={<AdminPanelSettings sx={{ fontSize: 18 }} />} iconPosition="start" />}
          </Tabs>
        </Box>

        {/* ── Tab 1: Usuarios del sistema ── */}
        {tab === 1 && <UsuariosSistema clientes={clientes} />}

        {/* ── Tab 2: Dispositivos POS (IMEI) ── */}
        {tab === 2 && <DispositivosPos clientes={clientes} />}

        {/* ── Tab 3: Franquiciados (solo superadmin) ── */}
        {tab === 3 && !esFranquiciado && <FranquiciadosSistema clientes={clientes} />}

        {/* ── Tab 0: Parqueaderos ── */}
        {tab === 0 && <>

        {/* Stats */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <StatCard icon={<LocalParking />}   label="Total clientes"   value={clientes.length}            loading={cargando} />
          <StatCard icon={<CheckCircle />}    label="Activos"          value={activos}                    loading={cargando} />
          <StatCard icon={<Cancel />}         label="Inactivos"        value={clientes.length - activos}  loading={cargando} />
          <StatCard icon={<TrendingUp />}     label="Nuevo este mes"   value={clientes.filter(c => { if (!c.created_at) return false; const d = new Date(c.created_at); const n = new Date(); return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear(); }).length} loading={cargando} />
        </Stack>

        {/* Toolbar */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap', alignItems: 'center', bgcolor: WHITE, p: 1.5, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <TextField size="small" placeholder="Buscar parqueadero..." value={busqueda} onChange={e => setBusqueda(e.target.value)} sx={{ flex: 1, minWidth: 200 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: TEXT_SEC }} /></InputAdornment>, sx: { borderRadius: 2, bgcolor: BG, '& fieldset': { borderColor: BORDER } } }} />

          <ToggleButtonGroup value={vista} exclusive onChange={(_, v) => v && setVista(v)} size="small">
            <ToggleButton value="grid" sx={{ borderRadius: '8px 0 0 8px', '&.Mui-selected': { bgcolor: alpha(ORANGE, 0.12), color: ORANGE, borderColor: alpha(ORANGE, 0.3) } }}><GridView fontSize="small" /></ToggleButton>
            <ToggleButton value="table" sx={{ borderRadius: '0 8px 8px 0', '&.Mui-selected': { bgcolor: alpha(ORANGE, 0.12), color: ORANGE, borderColor: alpha(ORANGE, 0.3) } }}><TableRows fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Actualizar">
            <IconButton onClick={cargar} disabled={cargando} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>

          <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}
            sx={{ bgcolor: ORANGE, color: WHITE, borderRadius: 2, fontWeight: 700, px: 2.5, '&:hover': { bgcolor: '#D97706' }, boxShadow: `0 4px 12px ${alpha(ORANGE, 0.35)}` }}>
            Nuevo parqueadero
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {/* Loading */}
        {cargando && (
          <Grid container spacing={2}>
            {[1,2,3,4].map(i => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Card elevation={0} sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: 2, height: 190, borderTop: `4px solid ${BORDER}` }}>
                  <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress size={24} sx={{ color: ORANGE }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Empty */}
        {!cargando && filtrados.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 10, bgcolor: WHITE, borderRadius: 2, border: `1px solid ${BORDER}` }}>
            <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: alpha(ORANGE, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <LocalParking sx={{ fontSize: 42, color: alpha(ORANGE, 0.5) }} />
            </Box>
            <Typography sx={{ color: TEXT_SEC, mb: 2 }}>{busqueda ? 'Sin resultados para esa búsqueda' : 'No hay parqueaderos registrados aún'}</Typography>
            {!busqueda && (
              <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}
                sx={{ bgcolor: ORANGE, color: WHITE, borderRadius: 2, fontWeight: 700, '&:hover': { bgcolor: '#D97706' } }}>
                Registrar primero
              </Button>
            )}
          </Box>
        )}

        {/* Grid */}
        {!cargando && filtrados.length > 0 && vista === 'grid' && (
          <Grid container spacing={2}>
            {filtrados.map(c => <Grid item xs={12} sm={6} md={4} lg={3} key={c.id}><ClienteCard cliente={c} onActualizar={cargar} clientes={clientes} puedeEliminar={!esFranquiciado} /></Grid>)}
          </Grid>
        )}

        {/* Tabla */}
        {!cargando && filtrados.length > 0 && vista === 'table' && (
          <Box sx={{ bgcolor: WHITE, borderRadius: 2, border: `1px solid ${BORDER}`, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#F8F8F8', color: TEXT_SEC, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, borderColor: BORDER } }}>
                  <TableCell>Parqueadero</TableCell>
                  <TableCell>API Key</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtrados.map(c => <ClienteRow key={c.id} cliente={c} onActualizar={cargar} puedeEliminar={!esFranquiciado} />)}
              </TableBody>
            </Table>
          </Box>
        )}

        </>}
      </Box>

      <CrearClienteDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onCreado={cargar} />
    </Box>
  );
}
